import "./ccPillars.css";

import everyStep from "../../../assets/every-step-cc.webp";
import customizationShot from "../../../assets/customization.webp";

import managementSystem from "../../../assets/Career-management-system.webp";
import careerCenterData from "../../../assets/career-center-data.webp";

/* The four reasons, one per row: copy and a button on one side, a tinted
   panel with the product shot on the other, and a hairline above each row.
   Rows alternate sides down the page — the same rhythm as the reference.

   Each row states the claim, then answers it in a paragraph rather than a
   stack of labelled payoffs — the objection a career centre raises is never
   "what is it" but "what does that get my office", and that argument reads
   better as prose than as a spec sheet. The four claims in order: it is one
   platform rather than five, it is a full career management system and not
   only student prep, the data comes out of it on its own, and all of it is
   configured to how your campus already advises. */

const rows = [
  {
    tone: "blue",
    eyebrow: "One platform, every step",
    title: "One login. No more platform fatigue.",
    body: [
      "Resume prep, interviews, outreach, applications, and advisor feedback all live in one place. Students sign into one tool instead of three, which is the reason they keep using it.",
      "Your team points to the same platform at every step, and each step feeds the next, so nobody starts over halfway through the search.",
    ],
    cta: "Book a walkthrough",
    href: "#book",
    /* This row claims "one platform, not five", so it shows the whole
       platform at once, running edge to edge with no inset. */
    image: everyStep,
    alt: "The RAIN platform: career exploration, resume and cover letter reviews, applications, networking, interview prep and advisor feedback, connected around the students using them",
    fill: true,
  },
  {
    tone: "violet",
    eyebrow: "A full career management system",
    title: "Everything your office runs, in the same system",
    /* The modules the office already runs, the reporting that comes off
       them, then the procurement line it ends on. "Experiential learning"
       rather than "on-campus jobs" — it is the module name in the product
       shot beside this copy. */
    body: [
      "Your job board, employer CRM, events, appointments, and experiential learning all run here, so postings and employers sit beside the activity they generate, and outcomes reporting comes out of the work itself.",
      "Employer relations, advising, and student employment share one student record. One contract, one login, one security review.",
    ],
    /* "See the full system" belongs on a button that goes to one. Until that
       page exists this lands on the booking form like the rest. */
    cta: "Book a walkthrough",
    href: "#book",
    image: managementSystem,
    alt: "RAIN career management system: job board, employer CRM, events and fairs, appointments, experiential learning, first destination survey, reports, classes and mentorship",
  },
  {
    tone: "teal",
    eyebrow: "More data, better decisions",
    title: "Full visibility across your student body",
    body: [
      "Students use RAIN at every stage of the search, so the data arrives on its own: every application, interview, and internship, not just where someone landed six months out.",
      "It’s real engagement, not a survey a fraction of them filled out. When leadership asks how the office is doing, you bring the whole journey instead of anecdotes and a first-destination PDF.",
    ],
    cta: "Book a walkthrough",
    href: "#book",
    image: careerCenterData,
    alt: "RAIN career center portal showing engagement, resume analytics and outcomes by major",
  },
  {
    tone: "coral",
    eyebrow: "Built around your institution",
    title: "RAIN is the backbone. What students see is up to you.",
    /* This row is about customization, full stop — branding, templates, and
       above all the AI standards, which is the part no other tool lets an
       office set. The illustration beside it reads as a white label; the
       copy must not say so, because RAIN is not white labeled. That is why
       the old line "students see your career center, not a vendor" came out:
       it claimed exactly the thing the picture only implies. Configure, not
       rebrand. */
    body: [
      "Your branding, your resume templates, your advising workflows, your resources, built for your office instead of a default we picked.",
      "The AI runs on your standards too. You set how it grades a resume and scores a mock interview, so the feedback students get between appointments is the feedback your advisors would have given. Nothing you build is shared with another school.",
    ],
    cta: "Book a walkthrough",
    href: "#book",
    image: customizationShot,
    alt: "A student-facing RAIN page carrying a school's own crest, colours and templates",
    fill: true,
  },
];

const CcPillars = () => (
  <section className="cc-section cc-pillars" id="how">
    <div className="home-container">
      {rows.map((row, i) => (
        <article
          key={row.eyebrow}
          /* Odd rows put the panel on the left, so the page zig-zags. */
          className={`cc-row cc-row-${row.tone}${i % 2 ? " cc-row-flip" : ""}`}
        >
          <div className="cc-row-copy">
            <span className="cc-row-eyebrow">{row.eyebrow}</span>
            <h3 className="cc-row-title">{row.title}</h3>
            {row.body.map((para) => (
              <p className="cc-row-body" key={para}>
                {para}
              </p>
            ))}

            <a className="cc-row-btn" href={row.href}>
              {row.cta}
            </a>
          </div>

          <div
            className={`cc-row-visual${
              row.fill ? " cc-row-visual-fill" : ""
            }`}
          >
            <img decoding="async" src={row.image} alt={row.alt} loading="lazy" />
          </div>
        </article>
      ))}
    </div>
  </section>
);

export default CcPillars;
